/**
 * Returns true when the app is running inside an Electron shell.
 *
 * Both preload scripts (riveros and selfhosted) expose window.riverOS via
 * contextBridge. This is the canonical, synchronous signal — no userAgent
 * sniffing, no process.env, no undefined window.electronAPI.
 */
export function isElectron(): boolean {
  return typeof window !== 'undefined' && !!(window as any).riverOS;
}

/**
 * True only for the Electron desktop app running on macOS — the one platform
 * with native traffic lights (`trafficLightPosition` in main.js) that the
 * desktop system bar's content has to be visually aligned against. Used by
 * `DesktopSystemBar` to apply `.systembarMacAlign`'s translateY offset (see
 * `$systembar-content-y-offset` in `DesktopShell.module.scss`).
 */
export function isMacElectron(): boolean {
  return isElectron() && (window as any).riverOS?.platform === 'darwin';
}

/**
 * Initiating client for agent runs. Sent as `clientKind` on every POST /agent/run
 * so tool-runtime can expose desktop tools only when the message came from Electron.
 */
export function getClientKind(): 'web' | 'electron' {
  return isElectron() ? 'electron' : 'web';
}

/**
 * Whether the agent's Electron "computer use" tools (screenshot, mouse, keyboard,
 * bash) should be exposed for this run. Driven purely by the OS-level permission
 * grants Electron actually reports — there is no separate on/off preference —
 * so enabling/disabling computer use is just a matter of granting or revoking
 * Accessibility and Screen Recording in System Settings. Non-macOS platforms have
 * no equivalent persistent OS gate for these tools, so they default to available;
 * RPA execution still fails gracefully at the point of use if the underlying
 * capability (e.g. Wayland portals) isn't actually ready.
 */
export async function getAllowComputerUse(): Promise<boolean> {
  const riverOS = (window as any).riverOS;
  if (!riverOS?.checkPermissions) return false;
  if (riverOS.platform !== 'darwin') return true;
  try {
    const result = await riverOS.checkPermissions();
    return !!(result?.accessibility?.granted && result?.screenRecording?.status === 'granted');
  } catch {
    return false;
  }
}

/**
 * True when local file system access is available via the River desktop preload.
 * Checks for the canonical riverOS.fileSystem API first (readFile + runShell),
 * then falls back to the electron.ipcRenderer.invoke wrapper.
 */
export function hasLocalFileSystemIPC(): boolean {
  if (typeof window === 'undefined') return false;
  // Prefer the proper riverOS API (exposed via contextBridge, no whitelist issues)
  if (typeof (window as any).riverOS?.fileSystem?.readFile === 'function') return true;
  return typeof (window as any).electron?.ipcRenderer?.invoke === 'function';
}
